import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuth } from "@/contexts/auth";
import { useOrganizationResourcesClient } from "@/features/credentials/hooks/use-organization-resources-client";
import { getListItems } from "@/features/credentials/lib/get-list-items";
import { uniqueBy } from "@/features/credentials/lib/unique-by";
import type { Entity } from "@/features/credentials/models/organization-resource";
import { organizationResourcesQueryKeys } from "@/features/credentials/queries/organization-resources-query-keys";

function normalizeEntities(items: Entity[]) {
  return uniqueBy(
    items.map((entity) => ({
      ...entity,
      entityId: String(entity.entityId ?? ""),
    })),
    (entity) => entity.entityId,
  );
}

export function useEntitiesListQuery({
  sport,
  organizationId,
  seasonIds,
  enabled,
}: {
  sport: string;
  organizationId: string;
  seasonIds: string[];
  enabled: boolean;
}) {
  const { productToken } = useAuth();
  const organizationResourcesClient = useOrganizationResourcesClient();
  const sortedSeasonIds = [...seasonIds].sort();

  return useQuery({
    queryKey: organizationResourcesQueryKeys.entities({
      sport,
      organizationId,
      seasonIds: sortedSeasonIds,
    }),
    queryFn: async () => {
      if (sortedSeasonIds.length === 0) {
        const response = await organizationResourcesClient.getEntities({
          productToken: productToken as string,
          sport,
          organizationId,
        });
        return normalizeEntities(getListItems(response));
      }

      const responses = await Promise.all(
        sortedSeasonIds.map((seasonId) =>
          organizationResourcesClient.getEntities({
            productToken: productToken as string,
            sport,
            organizationId,
            seasonId,
          }),
        ),
      );

      return normalizeEntities(responses.flatMap((response) => getListItems(response)));
    },
    enabled: enabled && !!productToken && !!sport && !!organizationId,
    placeholderData: keepPreviousData,
  });
}

export function useMissingEntityNamesQuery({
  sport,
  organizationId,
  entityIds,
  knownNamesById,
  enabled,
}: {
  sport: string;
  organizationId: string;
  entityIds: string[];
  knownNamesById: Map<string, string>;
  enabled: boolean;
}) {
  const { productToken } = useAuth();
  const organizationResourcesClient = useOrganizationResourcesClient();
  const missingIds = useMemo(
    () => [...entityIds].filter((entityId) => entityId && !knownNamesById.has(entityId)).sort(),
    [entityIds, knownNamesById],
  );

  return useQuery({
    queryKey: organizationResourcesQueryKeys.entitiesByIds({
      sport,
      organizationId,
      entityIds: missingIds,
    }),
    queryFn: async () => {
      const responses = await Promise.all(
        missingIds.map((entityId) =>
          organizationResourcesClient.getEntities({
            productToken: productToken as string,
            sport,
            organizationId,
            entityId,
            limit: 1,
          }),
        ),
      );

      return normalizeEntities(responses.flatMap((response) => getListItems(response)));
    },
    enabled: enabled && missingIds.length > 0 && !!productToken && !!sport && !!organizationId,
  });
}
